import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { LogOut, LayoutDashboard, MessageCircle, User, Inbox, BookMarked, Mail, ChevronDown, Menu, ShieldCheck } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const LOGO = "https://customer-assets-agu9un31.emergentagent.net/job_publishing-platform-2/artifacts/fbxxm2dm_C9FE46DB-E250-47E4-A350-1D05A33B01E0_L0_001-4_18_2026%2C%209_55_01%20AM.webp";

export function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!user) { setUnread(0); return; }
    let alive = true;
    const load = async () => {
      try { const { data } = await api.get("/conversations/unread-count"); if (alive) setUnread(data.unread || 0); } catch { /* ignore */ }
    };
    load();
    const t = setInterval(load, 10000);
    return () => { alive = false; clearInterval(t); };
  }, [user]);

  const dashPath = user?.role === "author" ? "/author" : "/reader";
  const campaignsPath = user?.role === "author" ? "/author" : "/campaigns";

  const MenuTrigger = (
    <Button
      variant="outline"
      className="btn-press flex items-center gap-2"
      style={{ borderColor: "var(--border-soft)" }}
      data-testid="nav-menu-btn"
    >
      <Menu size={16} />
      <span className="hidden sm:inline">Menu</span>
      <ChevronDown size={14} />
    </Button>
  );

  return (
    <header
      data-testid="app-navbar"
      className="sticky top-0 z-40 backdrop-blur-xl bg-black/70 border-b"
      style={{ borderColor: "var(--border-soft)" }}
    >
      <div className="max-w-6xl mx-auto flex items-center justify-between px-4 sm:px-6 h-16">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-home-link">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl overflow-hidden bg-gradient-to-br from-pink-300 to-fuchsia-500" style={{ border: "1px solid var(--border-soft)" }}>
            <img src={LOGO} alt="Faraway Sister Haven logo" className="h-full w-full object-cover" />
          </span>
          <span className="rainbow-text text-xl tracking-tight font-bold">
            Faraway Sister Haven
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-6 text-sm">
          <Link to="/campaigns" data-testid="nav-browse-link" className="hover:opacity-80">Browse Campaigns</Link>
          <a href="/#how" data-testid="nav-how-link" className="hover:opacity-80">How it Works</a>
        </nav>

        <div className="flex items-center gap-2">
          {!user ? (
            <>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>{MenuTrigger}</DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56" data-testid="nav-menu-guest">
                  <DropdownMenuItem onClick={() => nav("/campaigns")} data-testid="menu-campaigns">
                    <BookMarked size={14} className="mr-2" /> Browse Campaigns
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav("/contact")} data-testid="menu-contact">
                    <Mail size={14} className="mr-2" /> Contact Faraway Sister Haven
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                variant="ghost"
                className="btn-press hover:bg-white/5"
                onClick={() => nav("/login")}
                data-testid="nav-login-btn"
              >
                Log in
              </Button>
              <Button
                className="btn-press btn-rainbow rounded-full"
                onClick={() => nav("/register")}
                data-testid="nav-register-btn"
              >
                Join FSH
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                className="btn-press hover:bg-white/5 relative"
                onClick={() => nav("/messages")}
                data-testid="nav-messages-btn"
                title="Messages"
              >
                <MessageCircle size={18} />
                {unread > 0 && (
                  <span
                    className="absolute -top-1 -right-1 text-[10px] rounded-full px-1.5 py-0.5 font-bold"
                    style={{ background: "#E040FB", color: "#fff" }}
                    data-testid="nav-unread-badge"
                  >
                    {unread > 99 ? "99+" : unread}
                  </span>
                )}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>{MenuTrigger}</DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64" data-testid="nav-menu-user">
                  <DropdownMenuLabel className="truncate">
                    <span style={{ color: "var(--ink-soft)" }}>Signed in as</span>{" "}
                    <span className="rainbow-text font-bold">{user.display_name}</span>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => nav(dashPath)} data-testid="menu-dashboard">
                    <LayoutDashboard size={14} className="mr-2" /> Dashboard
                  </DropdownMenuItem>
                  {user.is_admin && (
                    <DropdownMenuItem onClick={() => nav("/admin")} data-testid="menu-admin">
                      <ShieldCheck size={14} className="mr-2" /> Admin console
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => nav("/profile")} data-testid="menu-profile">
                    <User size={14} className="mr-2" /> Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav("/messages")} data-testid="menu-messages">
                    <MessageCircle size={14} className="mr-2" /> Messages
                    {unread > 0 && (
                      <span className="ml-auto text-[10px] rounded-full px-1.5 py-0.5 font-bold" style={{ background: "#E040FB", color: "#fff" }}>
                        {unread > 99 ? "99+" : unread}
                      </span>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav(dashPath)} data-testid="menu-applications">
                    <Inbox size={14} className="mr-2" /> Applications
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav(campaignsPath)} data-testid="menu-campaigns">
                    <BookMarked size={14} className="mr-2" /> Campaigns
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => nav("/contact")} data-testid="menu-contact">
                    <Mail size={14} className="mr-2" /> Contact Faraway Sister Haven
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => { logout(); nav("/"); }} data-testid="menu-logout">
                    <LogOut size={14} className="mr-2" /> Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
